// Sugalarm — top-of-frame header. Shows the wordmark, mini current value,
// CGM provider + connection dot, alert badge, speaker (TTS), kiosk volume
// (cycle 22; long-press to mute, cycle 332), and clock.

import { useRef, useState } from "react";

import type { VolumeAgentState } from "../../hooks/useVolumeAgent";
import { useBatteryStatus } from "../../hooks/useBatteryStatus";
import { useWifiSignal } from "../../hooks/useWifiSignal";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import { displayValue, fmtAge, fmtTime, zoneColor } from "../../lib/glucose";
import type { Zone } from "../../lib/types";
import { TrendGlyphSvg } from "../glucose/TrendGlyphSvg";
import { ProviderPill } from "./ProviderPill";
import { MemberAvatar } from "../ui/MemberAvatar";
import { BatteryIndicator } from "./BatteryIndicator";
import { WifiIndicator } from "./WifiIndicator";
import { VolumeIcon } from "./VolumeIcon";
import { VolumePopover } from "./VolumePopover";

interface Props {
  /** ⚠ Cycle 435: the mini value is a `DisplayGlucose` — a value the
   *  selector (`lib/currentGlucose.ts`) released as CURRENT — or null, in
   *  which case the whole cluster is withheld. A plain `Reading` cannot be
   *  passed here; that is the enforcement. Until this cycle a stale value
   *  was greyed and still painted, which is still a number a caregiver
   *  reads. */
  current: DisplayGlucose | null;
  zone: Zone;
  ageMin: number;
  provider: string | null;
  unackCount: number;
  prediction?: string | null;
  now: number;
  h24: boolean;
  units: "mg/dL" | "mmol/L";
  /** Trend glyph for the mini readout (0.63.2). Replaces the "mg/dL"
   *  units span — caller resolves via `trendInfo(latest.trend, latest.trend_rate).glyph`.
   *  Pass empty string when there's no reading; the whole cluster is
   *  hidden by the `value != null` gate so it never renders. */
  trendGlyph: string;
  /** Kiosk-only — undefined or `available: false` hides the button slot. */
  volume?: VolumeAgentState;
  /** Cycle 317: hide the popover's Mute control (the Android tablet kiosk's
   *  native volume floors the level — a glucose-alarm kiosk must never be
   *  one-tap silenced). Default false → the Pi loopback agent keeps Mute. */
  volumeHideMute?: boolean;
  /** Cycle 34.3: active member display name shown in the top-left slot.
   *  Cycle 105: the slot is now the member avatar + name only — the
   *  relationship subtitle was removed from the header (the Now hero
   *  keeps it). */
  memberName?: string | null;
  /** Cycle 105: the active member's avatar id (null → Sugalarm shield). */
  memberAvatarId?: string | null;
}

export function KioskHeader({
  current,
  zone,
  ageMin,
  provider,
  unackCount,
  prediction,
  now,
  h24,
  units,
  trendGlyph,
  volume,
  volumeHideMute = false,
  memberName = null,
  memberAvatarId = null,
}: Props) {
  // A current value is by definition not stale; the cluster is withheld
  // outright when there is none.
  const heroColor = zoneColor(zone);
  // Cycle 229: real battery readout on the Android tablet kiosk only
  // (battery.available is true solely on the kiosk APK).
  const battery = useBatteryStatus();
  // Cycle 235.4: Wi-Fi signal bars beside the battery — Android tablet kiosk
  // only (wifi.available true solely on the kiosk APK).
  const wifi = useWifiSignal();
  const [volumeOpen, setVolumeOpen] = useState(false);
  const volumeButtonVisible = volume?.available === true && volume.state !== null;
  // Cycle 332: long-press the volume button to mute/unmute (a real toggle
  // on the Pi loopback agent; on the Android tablet it drops to — and
  // restores from — the existing cycle-317 alarm-safety floor). A plain
  // tap still opens the volume popover, unchanged. The timer is a ref
  // (not state) so it never triggers a re-render; `firedRef` suppresses
  // the popover-toggle that would otherwise follow the long-press's
  // pointerup/click.
  const volumeLongPressTimerRef = useRef<number | null>(null);
  const volumeLongPressFiredRef = useRef(false);
  const VOLUME_LONG_PRESS_MS = 550;
  const clearVolumeLongPressTimer = () => {
    if (volumeLongPressTimerRef.current != null) {
      window.clearTimeout(volumeLongPressTimerRef.current);
      volumeLongPressTimerRef.current = null;
    }
  };
  const onVolumePointerDown = () => {
    volumeLongPressFiredRef.current = false;
    clearVolumeLongPressTimer();
    volumeLongPressTimerRef.current = window.setTimeout(() => {
      volumeLongPressTimerRef.current = null;
      volumeLongPressFiredRef.current = true;
      if (volume && volume.state) {
        void volume.setMuted(!volume.state.muted);
      }
    }, VOLUME_LONG_PRESS_MS);
  };
  const onVolumeClick = () => {
    if (volumeLongPressFiredRef.current) {
      // The long-press already fired the mute toggle — swallow the tap
      // that follows the release so the popover doesn't also open.
      volumeLongPressFiredRef.current = false;
      return;
    }
    setVolumeOpen((v) => !v);
  };
  return (
    <div
      style={{
        // Cycle 57.2: header grew 44 → 64 to host the larger Logo.
        // Cycle 57.4 trims it 10% to 58 px per operator tuning; the
        // cycle-34 ResizeObserver chart absorbs the change.
        // Cycle 79: sourced from the shared `--kiosk-header-height` token
        // so the side-rail seam stays glued to the header's bottom edge.
        // The literal 58px fallback keeps the web frame (rendered outside
        // `.kiosk`, where the token is undefined) byte-identical.
        height: "var(--kiosk-header-height, 58px)",
        padding: "0 18px",
        // Cycle 103: the curved header↔rail seam is gone, so the bottom
        // hairline runs full width again on every kiosk screen (side mode
        // included). Web frame is unchanged.
        borderBottom: "0.5px solid var(--line)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        // Cycle 86: translucent on the kiosk so the theme gradient /
        // background shows through; the solid `var(--bg)` fallback keeps
        // the web-frame header (no `.kiosk` ancestor → token undefined)
        // byte-identical. The bottom hairline stays fully opaque.
        background: "var(--kiosk-chrome-fill, var(--bg))",
        flexShrink: 0,
        // Cycle 237: containing block for the absolutely-positioned avatar
        // medallion that overlaps the header's bottom hairline.
        position: "relative",
      }}
    >
      {/* Cycle 237: enlarged, ring'd circular member-avatar "medallion"
          that overlaps the header's bottom hairline and hangs ~14px into
          the content / nav-rail area below. Rendered once here, so it is
          identical on every kiosk screen and in both nav modes. The member
          NAME was removed from the header (the avatar now carries identity;
          the Now hero still shows the name above the glucose number).
          Absolutely positioned with z-index:5 so it lifts above plain
          content but stays BELOW every alarm surface (`.alarmBannerStrip`
          z-12, AlarmSilenceGuard z-60, UrgentOverlay z-100) — it can never
          cover an alarm banner/control. MemberAvatar (blob cache + shield
          fallback) is reused as-is. Side mode: the centered EdgeRail pill
          (cycle 103a) sits well below this top overhang, so no rail nudge
          is needed. */}
      <div
        style={{
          position: "absolute",
          left: 18,
          // Cycle 237.1: enlarged 64 → 76px per operator request. The extra
          // size hangs below the header so the medallion's top stays inside
          // the header band (not clipped at the frame top); the four kiosk
          // title rows widen their top clearance to match the content
          // overhang. Cycle 237.2: nudged down ~10px (bottom -20 → -30) per
          // operator request — ~30px content overhang now.
          bottom: -30,
          zIndex: 5,
          width: 76,
          height: 76,
          borderRadius: "50%",
          // Light ring + thin dark halo + soft drop shadow so the medallion
          // reads over both a bright wallpaper and the nav rail. Chrome
          // shadow rgba (no ink token applies — this is not text).
          boxShadow:
            "0 0 0 2.5px rgba(230, 242, 255, 0.92), 0 0 0 4px rgba(0, 0, 0, 0.35), 0 6px 16px rgba(0, 0, 0, 0.45)",
        }}
      >
        <MemberAvatar
          avatarId={memberAvatarId}
          name={memberName ?? "Sugar Alarm"}
          size={76}
          shape="circle"
        />
      </div>

      <div style={{ flex: 1 }} />

      {/* Mini current value — withheld entirely unless the selector
          released a current value (cycle 435). */}
      {current != null && (
        <div
          style={{
            display: "flex",
            // Cycle 57.5: was "baseline" — switched to "center" so the
            // mini value / units / age row vertically aligns with the
            // sibling Dexcom-status cluster (ConnDot + provider name),
            // which uses center alignment. They now share a baseline
            // visually instead of the age text floating low.
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            className="tnum"
            style={{
              fontFamily: "var(--hero-font)",
              fontWeight: 600,
              fontSize: 18,
              color: heroColor,
              letterSpacing: "-0.02em",
            }}
          >
            {displayValue(current.sgv, units)}
          </span>
          {/* 0.63.2: the "mg/dL" units pill that used to sit here was
              replaced with the trend arrow. 0.64.1 wrapped the
              Unicode glyph in a fixed 22×22 flex-centered box to pin
              its optical center against the number. 0.65.7 replaces
              the font glyph entirely with `<TrendGlyphSvg>` — an
              inline SVG path inside a 22×22 viewBox — so the arrow
              renders byte-identically across Pi Chromium / iOS
              WKWebView / Android WebView / desktop Chromium. Manrope
              draws `↑`, `↗`, `→`, `↘`, `↓`, `↓↓` at slightly
              different vertical positions across rendering engines;
              SVG paths don't have that problem. The hero
              `<TrendArrow>` on NowScreen stays font-based — it sits
              next to the much larger Manrope hero number and reads
              best as a font glyph at that scale. */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: zoneColor(zone),
              transition: "color 200ms",
            }}
            aria-hidden="true"
          >
            <TrendGlyphSvg glyph={trendGlyph} size={22} />
          </span>
          <span
            className="tnum"
            style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", marginLeft: 4 }}
          >
            {fmtAge(ageMin)}
          </span>
        </div>
      )}

      {/* Prediction pill (only if active) */}
      {prediction != null && (
        <div
          style={{
            fontSize: "var(--type-floor)",
            fontWeight: 600,
            color: "var(--accent)",
            background: "var(--accent-soft)",
            padding: "3px 8px",
            borderRadius: 999,
          }}
        >
          {prediction}
        </div>
      )}

      {/* Connection — pill, matching the mobile zone-pill aesthetic
          (green when connected/fresh, amber when stale). Cycle 109:
          extracted to <ProviderPill> so the mobile header reuses it. */}
      <ProviderPill provider={provider} stale={current == null} />

      {/* Alert badge */}
      {unackCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            background: "var(--zone-urgent-soft)",
            color: "var(--zone-urgent)",
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: "var(--type-floor)",
            fontWeight: 600,
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: 999,
              background: "currentColor",
            }}
          />
          {unackCount}
        </div>
      )}

      {/* Cycle 229: battery indicator — replaces the cycle-34.13 Speak-status
          button (the three-sine-wave glyph). Renders ONLY on the Android
          tablet kiosk (battery.available true solely on the kiosk APK); the
          Pi kiosk / iPad / web / mobile show nothing in this slot. Status
          indicator, not a control. */}
      {/* Cycle 235.4: Wi-Fi signal bars — sits just LEFT of the battery, same
          kiosk-APK-only gating. Tap flashes the % then it fades. */}
      {wifi.available && (
        <WifiIndicator
          connected={wifi.connected}
          level={wifi.level}
          percent={wifi.percent}
        />
      )}

      {battery.available && (
        <BatteryIndicator level={battery.level} charging={battery.charging} />
      )}

      {/* Volume (cycle 22) — only when the local agent is reachable */}
      {volumeButtonVisible && volume && volume.state && (
        <button
          onClick={onVolumeClick}
          onPointerDown={onVolumePointerDown}
          onPointerUp={clearVolumeLongPressTimer}
          onPointerLeave={clearVolumeLongPressTimer}
          onPointerCancel={clearVolumeLongPressTimer}
          title={
            volume.state.muted
              ? "Volume — muted (long-press to unmute)"
              : `Volume — ${volume.state.level}% (long-press to mute)`
          }
          aria-expanded={volumeOpen}
          style={{
            appearance: "none",
            border: "none",
            background: volumeOpen ? "var(--accent-soft)" : "transparent",
            color: volumeOpen ? "var(--accent)" : "var(--ink-3)",
            borderRadius: 999,
            padding: "4px 6px",
            minHeight: 28,
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            transition: "background 160ms, color 160ms",
          }}
        >
          <VolumeIcon level={volume.state.level} muted={volume.state.muted} size={22} />
        </button>
      )}

      {/* Time — cycle 57.5: bolder + larger so it reads at distance
          on the kitchen-counter kiosk panel. */}
      {/* Cycle 383: `data-voice-trigger` marks this as the kiosk's hidden
          dev-gated voice trigger (a ≥3 s long-press). The attribute is
          INERT — the only thing that reads it is a document-level listener
          that `useVoiceCommands` registers ONLY when
          localStorage.sugalarm_voice_dev === "1". With the flag unset (the
          production condition) no listener exists and the clock behaves
          exactly as before: no handler, no affordance, no visual change.
          Scaffolding — the wake word replaces it next cycle. */}
      <span
        className="tnum"
        data-voice-trigger="kiosk-clock"
        style={{
          fontSize: 22,
          color: "var(--ink)",
          fontWeight: 700,
          fontFamily: "var(--hero-font)",
          letterSpacing: "-0.01em",
        }}
      >
        {fmtTime(now, { h24 })}
      </span>

      {volumeOpen && volume?.state && (
        <VolumePopover
          state={volume.state}
          onSetLevel={(n) => void volume.setLevel(n)}
          onSetMuted={(m) => void volume.setMuted(m)}
          onClose={() => setVolumeOpen(false)}
          hideMute={volumeHideMute}
        />
      )}
    </div>
  );
}
